/**
 * Crisis Management. Displays a popup with information about the current crisis management.
 * - The popup can be closed by pressing the escape key or by clicking the close button.
 * - The popup will not be displayed again for the current session.
 * - The popup can be displayed based on a publish date and an expiration date.
 * @param component Instance of this _Friendation_ component
 */
const BasisCrisisManagement = (component: HTMLElement) => {
  /** Initial timeout for the component. Time in milliseconds */
  const initialTimeout = 1000;

  /** Session key where we store the visibility state */
  const sessionKey = 'basisCrisisManagementHidden';

  /** Listen for keyboard events */
  const onKeydown = (e: KeyboardEvent) => {
    if (e.code === 'Escape') hide();
  };

  /** Show the {@link component} */
  const show = () => {
    // If the session contains hide information, do not show the component
    if (sessionStorage.getItem(sessionKey)) return;

    // Enable the component
    component.inert = false;

    // Disable body scroll
    document.body.style.overflow = 'hidden';

    /** Container element */
    const container: HTMLElement = component.querySelector('.outerWrapper');
    container.tabIndex = -1;

    // Listen for Escape Key
    document.addEventListener('keydown', onKeydown);

    // Set focus to the component
    setTimeout(() => {
      container.focus();
    }, 1);
  };

  /** Hide the {@link component} */
  const hide = () => {
    console.log('Hide function called');

    // Disable the component
    component.inert = true;
    document.body.tabIndex = -1;

    // Enable body scroll
    document.body.style.overflow = '';

    // Remove keydown event listener
    document.removeEventListener('keydown', onKeydown);

    // Focus on body
    setTimeout(() => {
      document.body.focus();
      hideForTime();
    }, 1);
  };

  /**
   * Checks if the {@link component}s visibility is restricted by a publish datetime, meaning it should not be visible yet.
   * @returns `true` if the component should be visible based on the publish date, `false` otherwise
   */
  const isToBePublished = () => {
    // If the component does not have a publish date, return true because it has no restrictions
    if (!component.dataset.dateFrom?.length) return true;

    const publishDate = new Date(component.dataset.dateFrom);
    const currentDate = new Date();

    if (currentDate.getTime() >= publishDate.getTime()) {
      return true;
    }

    return false;
  };

  /**
   * Checks if the {@link component}s visibility is restricted by an expiry datetime, meaning it should not be visible anymore.
   */
  const isNotExpired = () => {
    // If the component does not have an expiration date, return true because it has no restrictions
    if (!component.dataset.dateUntil?.length) return true;

    const expiryDate = new Date(component.dataset.dateUntil);
    const currentDate = new Date();

    if (currentDate.getTime() <= expiryDate.getTime()) {
      return true;
    }

    return false;
  };

  /** Hide the component for a certain time */
  const hideForTime = () => {
    sessionStorage.setItem(sessionKey, 'true');
  };

  /** Close button */
  const closeButton: HTMLButtonElement =
    component.querySelector('.closeButton');

  closeButton.addEventListener('click', hide.bind(this));

  // remove hidden attribute. It is only set to prevent flickering on page load
  component.hidden = false;

  // Show the component on page load when data-enabled is true and session does not contain hide information
  if (component.dataset.enabled === 'true') {
    // Show the component if it is to be published and not expired
    if (isToBePublished() && isNotExpired()) {
      // Add initial timeout
      setTimeout(() => {
        // Show the component
        show();
      }, initialTimeout);
    }
  }
};

/** Selector for any instance of this _Friendation_ component */
const selector = '[is="basis-crisis-management"]';

/** Function that handles a single instance of this _Friendation_ component */
const classFunction: (component: HTMLElement) => void = BasisCrisisManagement;

/**
 * Friendation component
 */
export const aBasisCrisisManagement = () => {
  /** Instances of this Friendation component */
  const component: HTMLElement = document.querySelector(selector);

  if (component) classFunction.call(this, component);
};
